import { MulticallRetryFunction } from '../types'
import { GenericTokenList } from '../types/general'
import { fetchFluidFTokens } from './fluid'
import { fetchGearboxV3Pools } from './gearbox'
import { fetchMorphoVaults, fetchListaVaultsFromChain } from './morpho'
import { fetchSiloVaults } from './silo'
import { fetchEulerEarnVaults } from './euler-earn'
import { fetchTermMaxVaults } from './termmax'
import { fetchLstShareTokens } from './lst'
import { fetchSavingsVaults } from './savings'
import { fetchLagoonVaults } from './lagoon'
import { fetchAaveEarnVaults } from './aave-earn'
import { fetchUpshiftVaults } from './upshift'
import { fetchYearnVaults } from './yearn'
import { fetchHypercoreVaults } from './hypercore'
import { fetchGmxVaults } from './gmx'
import { fetchPendlePtMarkets, hasPendleMarkets } from './pendle'
import {
  fetchSpectraPtMarkets,
  hasSpectraMarkets,
} from './spectra/index'
import { getGmxApiHost } from './gmx/registry'
import { fetchGmxExecutionFees } from './gmx/pricing'
import { getEvmClient } from '@1delta/providers'
import type { FluidFTokens } from './fluid/types'
import type { GearboxV3Pools } from './gearbox/types'
import type { MorphoVaults } from './morpho/types'
import type { SiloVaults } from './silo/types'
import type { EulerEarnVaults } from './euler-earn/types'
import type { TermMaxVaults } from './termmax/types'
import type { LstShareTokens } from './lst'
import type { SavingsVaults } from './savings'
import type { LagoonVaults } from './lagoon'
import type { AaveEarnVaults } from './aave-earn'
import type { UpshiftVaults } from './upshift'
import type { YearnVaults } from './yearn'
import type { HypercoreVaults } from './hypercore'
import type { GmxVaults, GmxExecutionFees } from './gmx'
import type { PendlePtMarkets } from './pendle'
import type { SpectraPtMarkets } from './spectra/index'
import { buildVaultLookup, type VaultLookupEntry } from './lookup'
import { stampVaultClassification } from './classification'
import { stampVaultTermSheets } from '../terms/vault/stamp'

// Toggle to silence vault-pipeline warnings. Set to `true` when debugging
// fetch/convert failures.
const VAULTS_DEBUG = false
const warn = (...args: unknown[]) => {
  if (VAULTS_DEBUG) console.warn(...args)
}

/** Supported ERC-4626 vault providers. */
/**
 * Every vault provider, as a runtime list. `VaultProvider` is derived from it,
 * so the two cannot drift — and consumers that need to validate untrusted
 * input (`parseEarnUid` on an action route's query param) can pass this as
 * `knownProviders` instead of accepting any `vault.<x>` string.
 */
export const VAULT_PROVIDERS = [
  'fluid',
  'gearbox',
  'morpho',
  'lista',
  'silo',
  'euler-earn',
  'termmax',
  'lst',
  'savings',
  'lagoon',
  'aave-earn',
  'upshift',
  'yearn',
  'hypercore',
  'gmx',
  'pendle',
  'spectra',
] as const

export type VaultProvider = (typeof VAULT_PROVIDERS)[number]

/** Chain under which the Hyperliquid HyperCore (L1) vaults are surfaced.
 *  HyperCore is not an EVM chain; we attribute its vaults to the
 *  Hyperliquid umbrella chain (HyperEVM, 999) so a single
 *  `/v1/data/vaults?chainId=999` query returns both. */
export const HYPERCORE_PROVIDER_CHAIN = '999'

/**
 * Per-provider payload returned by `getVaultPublicDataAll`. Each entry is
 * present only when the matching provider was requested AND its fetch
 * resolved (not when the chain has no deployment — those return an empty
 * map, which is still recorded here).
 */
export interface VaultPublicDataAll {
  fluid?: FluidFTokens
  gearbox?: GearboxV3Pools
  morpho?: MorphoVaults
  /** Lista DAO earn vaults (Moolah-fork of MetaMorpho, BNB chain). Same
   *  `MorphoVaults` shape as `morpho`, but fetched on-chain via a dedicated
   *  path since the Morpho API/subgraph doesn't index the Lista fork. */
  lista?: MorphoVaults
  silo?: SiloVaults
  'euler-earn'?: EulerEarnVaults
  termmax?: TermMaxVaults
  lst?: LstShareTokens
  savings?: SavingsVaults
  lagoon?: LagoonVaults
  /** Aave Earn ("stable") vaults — curator-run ERC-4626 wrappers over an
   *  Aave v3 supply position. Discovered + priced via the Aave public
   *  GraphQL API, enriched on-chain for `totalSupply`/share price. Keyed by
   *  lowercased vault address. */
  'aave-earn'?: AaveEarnVaults
  /** Upshift curator-run ERC-4626-style yield vaults, sourced from the
   *  Upshift public REST listing. Keyed by lowercased receipt-token
   *  address. */
  upshift?: UpshiftVaults
  /** Yearn V3 vaults (VaultV3 + TokenizedStrategy ERC-4626). Discovered
   *  via the yDaemon API; keyed by lowercased vault address. */
  yearn?: YearnVaults
  /** Hyperliquid HyperCore (L1) perp/HLP vaults. Only populated for the
   *  Hyperliquid chain ({@link HYPERCORE_PROVIDER_CHAIN}). Keyed by
   *  lowercased vault address. */
  hypercore?: HypercoreVaults
  /** GMX V2 GM-market and GLV-vault pool tokens (perp-DEX liquidity).
   *  Only populated on chains where GMX is deployed (Arbitrum One,
   *  Avalanche C-Chain). Keyed by lowercased token address. */
  gmx?: GmxVaults
  /** Per-chain minimum GM/GLV execution fees (wei). Populated alongside
   *  `gmx`. Gas-price-derived, so treat as fresh-at-fetch estimates. */
  gmxExecutionFees?: GmxExecutionFees
  /** Pendle V2 Principal Tokens as fixed-rate earn products — one row per
   *  LIVE market, keyed by lowercased PT address. **Matured markets are
   *  never included** (an expired PT redeems at par, so its published fixed
   *  APY is stale by definition); pass `pendleIncludeExpired` to override,
   *  which only a holder-facing redeem flow should ever do. Not a share
   *  token: no `totalAssets`/`totalSupply`/share price, USD-denominated
   *  size, and excluded from `buildVaultLookup` — the GMX/HyperCore
   *  precedent. */
  pendle?: PendlePtMarkets
  /** Spectra V2 Principal Tokens as fixed-rate earn products — one row per
   *  LIVE market, keyed by lowercased PT address. Same instrument and same
   *  rules as `pendle` (matured markets never included; pass
   *  `spectraIncludeExpired` to override), over a rate-adjusted Curve
   *  StableSwap-NG pool instead of Pendle's own AMM. Not a share token:
   *  USD-denominated size, no share price, excluded from `buildVaultLookup`. */
  spectra?: SpectraPtMarkets
}

export interface GetVaultPublicDataAllOptions {
  /** Narrow Silo to a single protocol version (`v2` or `v3`). */
  siloProtocolVersion?: 'v2' | 'v3'
  /** Page size hint for Silo's GraphQL query. */
  siloLimit?: number
  /**
   * Include MATURED Pendle PT markets. Default `false`.
   *
   * Only a flow that services an existing holder (redeem a PT you already
   * own) should set this. Any listing, ranking or discovery surface must
   * leave it off: a matured PT still publishes its last pre-expiry implied
   * APY, and that number is not an offer.
   */
  pendleIncludeExpired?: boolean
  /**
   * Include MATURED Spectra PT markets. Default `false`.
   *
   * Same rule as {@link pendleIncludeExpired}, with one caveat worth knowing
   * before relying on it: Spectra's listing appears to drop matured markets
   * upstream, so this switch may return nothing at all. The filter stays
   * regardless — "the API already handles it" is an observation about today,
   * not a contract.
   */
  spectraIncludeExpired?: boolean
}

/**
 * Combined output of `getVaultPublicDataAll`: the rich per-provider
 * payload (`data`) plus a flat address-keyed lookup (`lookup`) that
 * downstream consumers (e.g. user-balance endpoints) use without
 * branching on provider key. Both views come from the same fetch —
 * the lookup is built in-process, no extra I/O.
 */
export interface VaultPublicDataResult {
  data: VaultPublicDataAll
  lookup: Map<string, VaultLookupEntry>
}

/**
 * Universal vault fetcher — the vaults-side analogue of
 * `getLenderPublicDataAll`.
 *
 * Dispatches each requested provider to its dedicated fetcher and runs
 * them concurrently. Sources are mixed (Fluid + Gearbox use multicall;
 * Morpho + Silo are HTTP GraphQL), so there's no API-vs-on-chain split
 * like the lender path — every provider runs in parallel and a single
 * provider's failure does not sink the others.
 *
 * @param chainId         target chain
 * @param providers       which providers to fetch (any subset of
 *                        `'fluid' | 'gearbox' | 'morpho' | 'silo' | 'euler-earn'`)
 * @param multicallRetry  provider-level multicall executor — used by
 *                        Fluid and Gearbox; ignored when only HTTP
 *                        providers are requested
 * @param prices          optional price map keyed by oracle key /
 *                        underlying address
 * @param tokenList       optional token list for hydrating `asset`
 *                        metadata on each parsed vault
 * @param options         provider-specific knobs (Silo version filter, …)
 *
 * @returns `VaultPublicDataAll` — one key per requested provider that
 *          resolved successfully; failed providers are omitted (and
 *          logged when `VAULTS_DEBUG` is on).
 */
export const getVaultPublicDataAll = async (
  chainId: string,
  providers: VaultProvider[],
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  options?: GetVaultPublicDataAllOptions,
): Promise<VaultPublicDataResult> => {
  const requested = new Set(providers)
  if (requested.size === 0) return { data: {}, lookup: new Map() }

  const tasks: Array<Promise<void>> = []
  const out: VaultPublicDataAll = {}

  if (requested.has('fluid')) {
    tasks.push(
      fetchFluidFTokens(chainId, multicallRetry, prices, tokenList)
        .then((res) => {
          out.fluid = res
        })
        .catch((e) => {
          warn(
            `[vaults] fluid fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  if (requested.has('gearbox')) {
    tasks.push(
      fetchGearboxV3Pools(chainId, multicallRetry, prices, tokenList)
        .then((res) => {
          out.gearbox = res
        })
        .catch((e) => {
          warn(
            `[vaults] gearbox fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  if (requested.has('morpho')) {
    tasks.push(
      fetchMorphoVaults(chainId, multicallRetry, prices, tokenList)
        .then((res) => {
          out.morpho = res
        })
        .catch((e) => {
          warn(
            `[vaults] morpho fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  // Lista DAO earn vaults are a Moolah fork the Morpho API/subgraph doesn't
  // index, so they get their own on-chain fetcher. Self-gates to chains with
  // a Lista deployment (returns `{}` with no multicall otherwise), so calling
  // it on every chain is a cheap no-op.
  if (requested.has('lista')) {
    tasks.push(
      fetchListaVaultsFromChain(chainId, multicallRetry, prices, tokenList)
        .then((res) => {
          out.lista = res
        })
        .catch((e) => {
          warn(
            `[vaults] lista fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  if (requested.has('silo')) {
    tasks.push(
      fetchSiloVaults(chainId, prices, tokenList, {
        protocolVersion: options?.siloProtocolVersion,
        limit: options?.siloLimit,
      })
        .then((res) => {
          out.silo = res
        })
        .catch((e) => {
          warn(
            `[vaults] silo fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  if (requested.has('euler-earn')) {
    tasks.push(
      fetchEulerEarnVaults(chainId, prices, tokenList, multicallRetry)
        .then((res) => {
          out['euler-earn'] = res
        })
        .catch((e) => {
          warn(
            `[vaults] euler-earn fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  if (requested.has('termmax')) {
    tasks.push(
      fetchTermMaxVaults(chainId, multicallRetry, prices, tokenList)
        .then((res) => {
          out.termmax = res
        })
        .catch((e) => {
          warn(
            `[vaults] termmax fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  if (requested.has('lst')) {
    tasks.push(
      fetchLstShareTokens(chainId, multicallRetry, prices, tokenList)
        .then((res) => {
          out.lst = res
        })
        .catch((e) => {
          warn(
            `[vaults] lst fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  if (requested.has('savings')) {
    tasks.push(
      fetchSavingsVaults(chainId, multicallRetry, prices, tokenList)
        .then((res) => {
          out.savings = res
        })
        .catch((e) => {
          warn(
            `[vaults] savings fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  if (requested.has('lagoon')) {
    tasks.push(
      fetchLagoonVaults(chainId, prices, tokenList)
        .then((res) => {
          out.lagoon = res
        })
        .catch((e) => {
          warn(
            `[vaults] lagoon fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  // Aave Earn is a GraphQL-API listing (Aave AaveKit) enriched on-chain.
  // Self-gates to chains with tracked curators/vaults (returns `{}` with no
  // HTTP or RPC call otherwise), so calling it on every chain is a cheap
  // no-op.
  if (requested.has('aave-earn')) {
    tasks.push(
      fetchAaveEarnVaults(chainId, multicallRetry, prices, tokenList)
        .then((res) => {
          out['aave-earn'] = res
        })
        .catch((e) => {
          warn(
            `[vaults] aave-earn fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  // Upshift is a global REST listing (one endpoint, all chains). Self-
  // gates to chains with an Upshift deployment (returns `{}` with no HTTP
  // call otherwise), so calling it on every chain is a cheap no-op.
  if (requested.has('upshift')) {
    tasks.push(
      fetchUpshiftVaults(chainId, prices, tokenList)
        .then((res) => {
          out.upshift = res
        })
        .catch((e) => {
          warn(
            `[vaults] upshift fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  if (requested.has('yearn')) {
    tasks.push(
      fetchYearnVaults(chainId, multicallRetry, prices, tokenList)
        .then((res) => {
          out.yearn = res
        })
        .catch((e) => {
          warn(
            `[vaults] yearn fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  // HyperCore is L1 (off-chain) — its `vaultDetails` reads are chain-
  // independent, so we only run them for the Hyperliquid chain rather
  // than on every chain's vault fetch.
  if (requested.has('hypercore') && chainId === HYPERCORE_PROVIDER_CHAIN) {
    tasks.push(
      fetchHypercoreVaults()
        .then((res) => {
          out.hypercore = res
        })
        .catch((e) => {
          warn(
            `[vaults] hypercore fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  // Pendle PTs come from one global REST listing (all chains in a single
  // response, cached in-isolate), so the per-chain call is a filter, not a
  // round trip. Self-gates to chains with a Pendle deployment.
  if (requested.has('pendle') && hasPendleMarkets(chainId)) {
    tasks.push(
      fetchPendlePtMarkets(chainId, multicallRetry, prices, tokenList, {
        includeExpired: options?.pendleIncludeExpired,
      })
        .then((res) => {
          out.pendle = res
        })
        .catch((e) => {
          warn(
            `[vaults] pendle fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  // Spectra PTs come from a PER-NETWORK REST listing (there is no global
  // endpoint), cached in-isolate per network. Self-gates to chains with a
  // Spectra deployment — and that gate is a hand-maintained name map, since
  // the API routes on a network NAME and publishes no way to enumerate them.
  if (requested.has('spectra') && hasSpectraMarkets(chainId)) {
    tasks.push(
      fetchSpectraPtMarkets(chainId, multicallRetry, prices, tokenList, {
        includeExpired: options?.spectraIncludeExpired,
      })
        .then((res) => {
          out.spectra = res
        })
        .catch((e) => {
          warn(
            `[vaults] spectra fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
  }

  // GMX V2 (GM markets + GLV vaults) is a REST-API listing, deployed only
  // on Arbitrum One / Avalanche C-Chain. Skip the fetch entirely on other
  // chains rather than issuing a doomed request.
  if (requested.has('gmx') && getGmxApiHost(chainId)) {
    // Listing (API + on-chain USD enrichment via multicall).
    tasks.push(
      fetchGmxVaults(chainId, multicallRetry)
        .then((res) => {
          out.gmx = res
        })
        .catch((e) => {
          warn(
            `[vaults] gmx fetch failed for chain ${chainId}:`,
            (e as any)?.message ?? e,
          )
        }),
    )
    // Per-chain execution-fee minimums (gas-price × DataStore gas limits).
    tasks.push(
      (async () => {
        const gasPrice = await getEvmClient(chainId).getGasPrice()
        const fees = await fetchGmxExecutionFees(
          chainId,
          multicallRetry,
          gasPrice,
        )
        if (fees) out.gmxExecutionFees = fees
      })().catch((e) => {
        warn(
          `[vaults] gmx execution-fee fetch failed for chain ${chainId}:`,
          (e as any)?.message ?? e,
        )
      }),
    )
  }

  await Promise.all(tasks)
  // Classify every vault (yield-bearing vs volatile + stable/volatile
  // denomination) on the provider objects, so both the per-provider data
  // and the lookup carry the fields. Must run before buildVaultLookup,
  // which copies them across.
  stampVaultClassification(out, chainId, tokenList)
  // Then the structured deal — rate, maturity, exit, fees, backing,
  // counterparty, availability and principal risk — in the same shape a
  // lending market uses. Runs SECOND because it reads `yieldProfile`, which
  // the classification pass above produces.
  stampVaultTermSheets(out, chainId)
  // Build the flat address-keyed lookup once, here, so every caller
  // gets both views without re-traversing the per-provider maps.
  return { data: out, lookup: buildVaultLookup(out) }
}
